import { Anchor, Group, Stack, Text } from '@mantine/core';
import { FC, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PatientAnnotationNote } from './usePatientNotes';
import { formatHumanName } from '@medplum/core';
import dayjs from 'dayjs';
import { useMedplumProfile } from '@medplum/react';

// -----------------------------------------------------------------------------
interface NotePreviewItemProps {
  note: PatientAnnotationNote;
}

export const NotePreviewItem: FC<NotePreviewItemProps> = ({ note }) => {
  const profile = useMedplumProfile();
  const { t } = useTranslation(['patient-pages', 'common']);

  const [expanded, setExpanded] = useState(false);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const formattedDate = dayjs(note.timestamp).format('DD/MM/YYYY HH:mm');

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }

    // If content is taller than the clamped line, it is truncated
    setIsOverflowing(el.scrollHeight > el.clientHeight);
  }, [note.content]);

  return (
    <Stack gap={4}>
      <Group justify="space-between">
        <Text size="sm" fw={500}>
          {note.authorName || formatHumanName(profile?.name?.[0])}
        </Text>
        <Text size="xs" c="dimmed">
          {formattedDate}
        </Text>
      </Group>
      <Text
        ref={contentRef}
        lineClamp={expanded ? undefined : 2}
        size="sm"
        c="gray.8"
      >
        {note.content || (
          <Text c="dimmed" fs="italic">
            {t('notes.emptyNote', {
              ns: 'patient-pages',
              defaultValue: 'Empty note',
            })}
          </Text>
        )}
      </Text>
      {isOverflowing && (
        <Anchor onClick={() => setExpanded((v) => !v)} c="gray" size="xs">
          {expanded
            ? t('notes.less', {
                ns: 'patient-pages',
                defaultValue: '- Less',
              })
            : t('notes.more', {
                ns: 'patient-pages',
                defaultValue: '+ More',
              })}
        </Anchor>
      )}
    </Stack>
  );
};
